import clsx from 'clsx';
import { Content } from 'src/contexts/ContentContext';
import { useSiteTimezoneContext } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import { vars } from 'src/core/themes';
import { CheckIcon, IconsFill } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  DateRangePresetName,
  DateRangeRelativePreset,
  formatRelativeDateRange,
  getDateRangeRelativePresets,
  getRelativePresetFromUiDateTimeRange,
} from 'src/utils/dateTimeUtils';
import { DateTimeRangeWithRelative } from 'src/WebApiController';

import * as styles from './DateRangePresets.css';

export type DateRangeRelativePresetsProp = {
  presetNames: DateRangePresetName[];
  selectedDateRange?: DateTimeRangeWithRelative | null;
  onClick: (dateRange: DateTimeRangeWithRelative) => void;
  disabled?: boolean;
  /**
   * If true, the site's timezone will be applied to the date being displayed.
   * the underlying date value (value, data, onDateChange) will not be changed.
   */
  useSiteTimeZone?: boolean;
  hideSuggestionTitle?: boolean;
  variant?: 'default' | 'v2';
};

export function DateRangeRelativePresets({
  selectedDateRange,
  presetNames,
  onClick,
  disabled,
  useSiteTimeZone = true,
  hideSuggestionTitle,
  variant = 'default',
}: DateRangeRelativePresetsProp) {
  const { toZonedTime } = useSiteTimezoneContext();
  const isV2Variant = variant === 'v2';

  const handleClick = (preset: DateRangeRelativePreset) => {
    onClick(preset.range);
  };

  const presets = getDateRangeRelativePresets(presetNames);
  const presetName = getRelativePresetFromUiDateTimeRange(
    selectedDateRange,
    presetNames
  )?.name;

  return (
    <div className={styles.suggestionsContainer}>
      {!hideSuggestionTitle && (
        <header className={styles.suggestionsHeading}>
          <Content id={ContentId.DateRangeSuggestionsTitle} />
        </header>
      )}
      {presets.map((preset, index) => {
        const isSelected = presetName === preset.name;

        if (isV2Variant) {
          return (
            <div
              key={index}
              onClick={() => handleClick(preset)}
              className={clsx(styles.suggestionContainerV2, {
                [styles.suggestionV2Selected]: isSelected,
              })}
            >
              <>
                <button className={styles.suggestionV2} disabled={disabled}>
                  <span className={styles.suggestionTextV2}>
                    {preset.label}
                  </span>
                  <span className={styles.suggestionDate}>
                    (
                    {formatRelativeDateRange(
                      preset.range,
                      useSiteTimeZone ? toZonedTime : undefined
                    )}
                    )
                  </span>
                </button>
                {isSelected && (
                  <CheckIcon
                    className={styles.checkIconV2}
                    size={vars.iconSize.s}
                    fill={IconsFill.textSuccess}
                  />
                )}
              </>
            </div>
          );
        }
        return (
          <div
            key={index}
            onClick={() => handleClick(preset)}
            className={styles.suggestionContainer}
          >
            <button
              className={clsx(styles.suggestion, {
                [styles.suggestionSelected]: isSelected,
              })}
              disabled={disabled}
            >
              <span className={styles.suggestionText}>{preset.label}</span>{' '}
              <span>
                (
                {formatRelativeDateRange(
                  preset.range,
                  useSiteTimeZone ? toZonedTime : undefined
                )}
                )
              </span>
            </button>
          </div>
        );
      })}
    </div>
  );
}
